import { memo, useRef, useEffect } from "react";
import { Box, Typography } from "@mui/material";

interface PDFViewerProps {
    url?: string | null;
    height?: string | number;
    width?: string | number;
    page?: number;
}

const PDFViewer = memo(({ url, page, height = "740px", width = "100%" }: PDFViewerProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Build the source url (adds "#page" anchor when requested)
    const src = page && url ? `${url}#page=${page}` : url;

    // Simple Safari detection (avoids matching Chrome on iOS/macOS)
    const isSafari = typeof window !== "undefined" && /safari/i.test(navigator.userAgent) && !/chrome|crios|chromium|android/i.test(navigator.userAgent);

    // If page changes at runtime, update the hash for non-Safari browsers (Safari ignores this in <object>)
    useEffect(() => {
        if (!isSafari && iframeRef.current && page) {
            iframeRef.current.contentWindow?.location.replace(`${url}#page=${page}`);
        }
    }, [url, page, isSafari]);

    if (!url) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={height} bgcolor="background.paper" borderRadius={1} border={1} borderColor="divider">
                <Typography variant="body1" color="text.secondary">
                    No PDF available for this document.
                </Typography>
            </Box>
        );
    }

    // Safari has a rather poor <iframe> PDF implementation (missing scrollbars, incorrect sizing, etc.).
    // Switching to <object> drastically improves the experience while leaving other browsers untouched.
    if (isSafari) {
        return (
            <object
                data={src || undefined}
                type="application/pdf"
                width={width}
                height={height}
                style={{
                    border: "none",
                    borderRadius: "8px",
                }}
                aria-label="PDF preview"
            />
        );
    }

    // Default rendering for all other browsers
    return (
        <iframe
            ref={iframeRef}
            src={src || undefined}
            width={width}
            height={height}
            style={{
                border: "none",
                borderRadius: "8px",
            }}
            title={`PDF (page ${page || 1})`}
        />
    );
});

export default PDFViewer;
